import { useCallback, useEffect, useState } from 'react';
import { checkedVideoIdsSelector, unlockedVideoIdsSelector, } from '@core/gatedVideo/model';
import { isFalsy } from '@core/helpers/boolean';
import { useGlobalStore } from '@core/hooks/useGlobalStore';
import { useVideoGate } from './useVideoGate';
/**
 * Takes a video and returns its locked state
 */
export function useVideoIsLocked(video) {
    const { unlock } = useVideoGate();
    const videoId = video?.encoded_id || '';
    const unlockedVideoIds = useGlobalStore(unlockedVideoIdsSelector);
    const checkedVideoIds = useGlobalStore(checkedVideoIdsSelector);
    const videoIsChecked = useCallback((video) => {
        if (!video) {
            return false;
        }
        return checkedVideoIds.has(video.encoded_id);
    }, [checkedVideoIds]);
    const [videoIsLocked, setVideoIsLocked] = useState(() => {
        if (!video) {
            return false;
        }
        if (isFalsy(video.has_access_code)) {
            return false;
        }
        //video is not unlocked or has not been checked
        return (unlockedVideoIds[videoId] === undefined ||
            !checkedVideoIds.has(video.encoded_id));
    });
    const tryUnlockVideo = useCallback(async (video) => {
        if (!video || isFalsy(video.has_access_code)) {
            return false;
        }
        // user hasn't tried to unlock yet
        if (unlockedVideoIds[video.encoded_id] === undefined) {
            return true;
        }
        const isChecked = videoIsChecked(video);
        // user has already unlocked this video, re-auth
        const isLocked = isChecked
            ? false
            : !(await unlock(unlockedVideoIds[video.encoded_id], video.encoded_id));
        return isLocked;
    }, [unlockedVideoIds, unlock, videoIsChecked]);
    useEffect(() => {
        if (video) {
            tryUnlockVideo(video).then((isLocked) => {
                setVideoIsLocked(isLocked);
            });
        }
        else {
            setVideoIsLocked(false);
        }
    }, [video, tryUnlockVideo, setVideoIsLocked, videoIsLocked]);
    return videoIsLocked;
}
